import CloseIcon from '@mui/icons-material/Close';
import { Modal } from '@mui/material';
import Loading from 'components/Loading';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';
import SentryWrap from 'components/Sentry';
import { InfoOutlined } from '@mui/icons-material';
import { useStandards } from 'store/story/selectors';
import { formatStandardDisplay } from 'utils/lib/standardHelper';
import { useDispatch } from 'react-redux';
import StandardsModal from 'components/StandardsModal';
import { useOrgStandardCodes } from 'store/organizations/selectors';

const ViewStudentDetail = ({ open, onCancel, data, userId, funcObj }) => {
  const { handleGetAssignmentScore } = funcObj;
  const [assignmentScore, setAssignmentScore] = useState(null);
  const dispatch = useDispatch();
  const standards = useStandards();
  const enabledStateCodes = useOrgStandardCodes();
  const [standardsModal, setStandardsModal] = useState({
    open: false,
    standardAbbrs: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAssignmentScore = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const payload = { studentId: userId, assignment_id: data?.assignmentId };
      const response = await handleGetAssignmentScore(payload);
      setAssignmentScore(response?.data?.questionStatus || []);
    } catch (err) {
      setError('Failed to load assignment data');
      setAssignmentScore([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAssignmentScore();
    }
  }, [data, open]);



  const handleClose = () => {
    setAssignmentScore(null);
    onCancel();
  };
    
  const handleStandardInfoClick = (standardId) => {
    if (!standardId || standardId === 'N/A') return;
    const standardAbbrs = typeof standardId === 'string' && standardId.includes(',')
      ? standardId.split(',').map(s => s.trim()).filter(s => s)
      : [standardId];
    setStandardsModal({ open: true, standardAbbrs });
  };



  return (
    <SentryWrap>
    <>
      <Modal
        open={open}
        onClose={handleClose}
        style={{ height: '100%', width: '100%', backgroundColor: 'transparent' }}
      >
        <ModalContent>
          <ModalHeader className="relative">
            <CloseIcon
              onClick={handleClose}
              color="error"
              style={{ position: 'absolute', right: 5, top: 3, cursor: 'pointer' }}
            />
            <Container variant="contained">Standards Passed and Failed</Container>

            <Grid>
              {assignmentScore && assignmentScore.length > 0 ? (
                Array.from({ length: Math.ceil(assignmentScore.length / 2) }, (_, rowIndex) => {
                  const startIndex = rowIndex * 2;
                  const rowQuestions = assignmentScore.slice(startIndex, startIndex + 2);

                  return (
                    <Row key={rowIndex}>
                      {rowQuestions.map((question, index) => {
                        const questionNumber = startIndex + index + 1;
                        const isPassed = question?.pass;
                        const standardId = question?.standard_id || 'N/A';
                        const questionText = question?.question || 'Question';

                        const formattedStandardId = standardId !== 'N/A' 
                          ? formatStandardDisplay(standardId, standards, enabledStateCodes) || standardId
                          : standardId;

                        return (
                          <Cell className="break-word standardExamScoreModal" key={questionNumber} isFailed={!isPassed}>
                            {questionNumber}.{isPassed ? 'Passed' : 'Failed'} (Standard):&nbsp;
                            {formattedStandardId}
                            {standardId !== 'N/A' && (
                              <InfoIconWrapper
                                onClick={() => handleStandardInfoClick(standardId)}
                                onTouchStart={() => handleStandardInfoClick(standardId)}
                              >
                                <InfoOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                              </InfoIconWrapper>
                            )}
                          </Cell>
                        );
                      })}
                      {rowQuestions.length === 1 && (
                        <Cell isFailed={false} style={{ visibility: 'hidden' }}>
                          &nbsp;
                        </Cell>
                    )}
                  </Row>
                );
              })
            ) : (
              <Row>
                <Cell
                  isFailed={error ? true : false}
                  style={{
                    width: '100%',
                    justifyContent: 'center',
                    color: error ? '#dc3545' : '#666',
                  }}
                >
                  {isLoading ? <Loading size={20} /> : error || 'No questions found'}
                </Cell>
              </Row>
            )}
          </Grid>
        </ModalHeader>
      </ModalContent>
    </Modal>
      <StandardsModal
        open={standardsModal.open}
        onClose={() => setStandardsModal({ open: false, standardAbbrs: [] })}
        standardAbbrs={standardsModal.standardAbbrs}
        standards={standards}
        enabledStateCodes={enabledStateCodes}
      />
    </>
    </SentryWrap>
  );
};

export default ViewStudentDetail;

const Cell = styled.div`
  height: fit-content;
  max-height: 30px;
  flex: 1;
  margin: 0 5px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 500;
  color: ${(props) => (props.isFailed ? '#de0d14' : "#121212")};
  text-align: left;
`;

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  cursor: pointer;
  vertical-align: middle;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-bottom: 5px;
  width: 100%;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  margin-top: 5px;
`;

const Container = styled.div`
  width: 100%;
  padding: 10px;
  background: #010a54;
  color: ${COLOR.white};
  // margin-top: 22px;
  width: 250px;
  border-radius: 10px;
  margin-bottom: 10px;
`;

const ModalHeader = styled.div`
  background: #d9d9d9;
  padding: 20px 10px;
  font-size: 16px;
  font-weight: 500;
  width: 100%;
  border-radius: 10px;
  margin-top: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const ModalContent = styled.div`
  align-items: center;
  background-color: transparent;
  border-radius: 3px;
  display: inline-flex;
  flex-direction: column;
  left: 50%;
  width: 550px;
  max-height: 500px;
  overflow-y: auto;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`;
